import { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { useAuthConfig } from "./useAuthConfig";

export const useFetchData = (
  url,
  {
    params,
    enabled = true,
    transform,
    dependencies = [],
  } = {}
) => {
  const config = useAuthConfig();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(Boolean(url && enabled));
  const [error, setError] = useState(null);
  const transformRef = useRef(transform);
  const isMountedRef = useRef(true);

  useEffect(() => {
    transformRef.current = transform;
  }, [transform]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const fetchData = useCallback(async () => {
    if (!url || !enabled) {
      if (isMountedRef.current) setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await axios.get(url, {
        ...config,
        params,
      });
      const result = transformRef.current ? transformRef.current(response.data) : response.data;
      if (isMountedRef.current) setData(result);
    } catch (err) {
      console.error("Error fetching data:", err);
      if (isMountedRef.current) setError(err.message || "Failed to fetch data");
    } finally {
      if (isMountedRef.current) setLoading(false);
    }
  }, [url, enabled, config, JSON.stringify(params), ...dependencies]);

  useEffect(() => {
    const run = async () => {
      await fetchData();
    };

    if (enabled && url) run();
  }, [fetchData, enabled, url]);

  return { data, loading, error, refetch: fetchData };
};
